import { z } from "zod";

/**
 * What the visitor has agreed to store on their device.
 *
 * UK PECR (and the EU ePrivacy Directive) let us store only what is strictly
 * necessary without asking. Two things we do are neither:
 *
 * - **Analytics.** PECR gained a statistical-purposes exception on 5 February
 *   2026 (Data (Use and Access) Act 2025, Schedule A1) that would let
 *   first-party analytics run on an opt-out. We cannot use it: it requires
 *   aggregate, non-identifying data, and `analytics-identity.tsx` sends
 *   PostHog a real user id, name and email and turns on session replay. It is
 *   also UK-only, and we sell into the EU.
 * - **Creator Partner referral attribution.** Commercial attribution, not
 *   statistics. No exception covers it anywhere.
 *
 * Everything else we store is exempt and therefore absent from this record:
 * the session cookie, Turnstile's anti-abuse cookie and the checkout intent are
 * strictly necessary, and `theme` falls under the same Act's appearance limb.
 * So there are exactly two switches, and both default to off.
 */
export type ConsentState = {
  analytics: boolean;
  /** False until the visitor has actually chosen. Never assume from silence. */
  decided: boolean;
  referral: boolean;
};

const STORAGE_KEY = "pd.cookie-consent";

/**
 * How long a choice stands before we ask again.
 *
 * The ICO expects consent to be refreshed rather than collected once and
 * relied on forever; six to twelve months is the conventional range and we
 * take the longer end, because re-prompting is itself an annoyance.
 */
const CONSENT_MONTHS = 12;
const CONSENT_MS = CONSENT_MONTHS * 30 * 24 * 60 * 60 * 1000;

/**
 * The stored shape, parsed rather than trusted — the same reasoning as
 * `creator-referral.ts`: the value outlives deploys, so tomorrow's code meets
 * yesterday's record, and anything on the page can write to the key.
 *
 * `version` is a literal, so a record written by a future schema fails the
 * parse and is treated as no decision. Re-asking is the safe direction; acting
 * on a record we cannot read is not.
 */
const storedConsentSchema = z.object({
  analytics: z.boolean(),
  decidedAt: z.number(),
  referral: z.boolean(),
  version: z.literal(1),
});

/**
 * The answer when nothing has been decided — a frozen singleton, because
 * `useSyncExternalStore` compares snapshots by identity and a fresh object
 * every render is an infinite loop.
 */
const UNDECIDED: ConsentState = Object.freeze({
  analytics: false,
  decided: false,
  referral: false,
});

const CHANGE_EVENT = "pd:cookie-consent";

/**
 * Every access is wrapped: Safari's private mode throws on `localStorage`
 * rather than returning null, and a storage failure must never be able to
 * break a page — least of all the one asking for consent.
 */
function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

type ParsedConsent = {
  /** When this decision stops counting, or null when there is none to expire. */
  expiresAt: number | null;
  state: ConsentState;
};

function parseConsent(raw: string | null, now: number): ParsedConsent {
  const undecided = { expiresAt: null, state: UNDECIDED };

  if (!raw) {
    return undecided;
  }

  let parsedJson: unknown;

  try {
    parsedJson = JSON.parse(raw);
  } catch {
    return undecided;
  }

  const parsed = storedConsentSchema.safeParse(parsedJson);

  if (!parsed.success) {
    return undecided;
  }

  // A clock moved backwards would otherwise make `age` negative and keep a
  // stale decision alive indefinitely — the same guard `readCreatorReferral`
  // uses on the referral window.
  const age = now - parsed.data.decidedAt;

  if (age < 0 || age > CONSENT_MS) {
    return undecided;
  }

  return {
    expiresAt: parsed.data.decidedAt + CONSENT_MS,
    state: {
      analytics: parsed.data.analytics,
      decided: true,
      referral: parsed.data.referral,
    },
  };
}

/**
 * Snapshot cache, keyed on the stored text AND on the decision still being in
 * date.
 *
 * `getSnapshot` runs on every render, and `useSyncExternalStore` compares by
 * identity — a new object each call is an infinite render loop, not a subtle
 * inefficiency. So the same reference is returned while nothing has changed.
 *
 * Expiry is the half that is easy to miss: a decision lapses with the clock
 * rather than with a write, so keying on the text alone would let a snapshot
 * outlive its own validity.
 */
let cachedRaw: string | null = null;
let cachedState: ConsentState = UNDECIDED;
let cachedExpiresAt: number | null = null;
let cachePrimed = false;

function getSnapshot(): ConsentState {
  if (typeof window === "undefined") {
    return UNDECIDED;
  }

  const raw = readRaw();
  const now = Date.now();
  // The stored text alone is not enough to key the cache: a decision expires
  // with the clock, not with a write, so a snapshot can outlive its own
  // validity while the text sits unchanged.
  const unexpired = cachedExpiresAt === null || now < cachedExpiresAt;

  if (cachePrimed && raw === cachedRaw && unexpired) {
    return cachedState;
  }

  const parsed = parseConsent(raw, now);

  cachedRaw = raw;
  cachedState = parsed.state;
  cachedExpiresAt = parsed.expiresAt;
  cachePrimed = true;

  return cachedState;
}

function getServerSnapshot(): ConsentState {
  return UNDECIDED;
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  // Two sources: our own writes, and a choice made in another tab. Without the
  // second, accepting in one tab leaves the banner sitting in the others.
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);

  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

function notify() {
  cachePrimed = false;
  cachedExpiresAt = null;
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** The current choice. Safe during SSR, where it reports "not decided". */
export function readConsent(): ConsentState {
  return getSnapshot();
}

/** Record a choice. Both categories are always written, never merged. */
export function writeConsent(choice: {
  analytics: boolean;
  referral: boolean;
}) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        analytics: choice.analytics,
        decidedAt: Date.now(),
        referral: choice.referral,
        version: 1,
      }),
    );
  } catch {
    // No storage, no record — we will ask again next visit, which is the
    // conservative failure: nothing non-essential runs until we have a yes.
  }

  notify();
}

/** Forget the decision entirely, so the banner asks again. */
export function clearConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the record expires on its own.
  }

  notify();
}

export const cookieConsentStore = {
  getServerSnapshot,
  getSnapshot,
  subscribe,
};
